import { isBefore } from 'date-fns';
import qs from 'querystring';
import { parseLabelFromString } from 'contexts';
import { isUserLicensed, UserRoles } from '@bb/users';
import { utcToZonedTime } from 'date-fns-tz';
import {
  isAssumeOrgType,
  isAuthForRole,
  featureEnabled,
  formatPhoneNumber,
  getTimezoneCode,
  isFullAccessUser,
  capitalize,
  parseAccessHours,
  isManualCoopUserWithAccess,
} from 'utils';
// TODO: APP-3570 Remove when rets2 decodes
import { decode } from 'html-entities';
import { Modal } from 'components';
import { UNVERIFIED_AGENT_MODAL } from '@app/constants';
import { store } from 'components/store/store';
import { updateChatButtonSettings } from 'components/store/chatButtonRedux';

angular.module('bb.listing_view').component('listingView', {
  controller: ListingViewController,
  controllerAs: 'vm',
  template: require('./listing_view.html'),
  bindings: {
    listing: '=',
    listingInfo: '=',
    external: '=',
    // Used for viewListing page which uses lid
    isViewListing: '=',

    network: '=',
    assets: '=?',
  },
});

ListingViewController.$inject = [
  'ListingViewService',
  'UtilService',
  '$q',
  '$rootScope',
  'ModalService',
  'AppointmentConfigService',
  'AppointmentBookService',
  'appointmentListsService',
  '$location',
  'ErrorService',
  '$routeParams',
];

function ListingViewController(
  ListingViewService,
  UtilService,
  $q,
  $rootScope,
  ModalService,
  AppointmentConfigService,
  AppointmentBookService,
  appointmentListsService,
  $location,
  ErrorService,
  $routeParams,
) {
  const vm = this;

  // APP-1894 Hide no rooms
  vm.existingRooms = [];

  vm.$onDestroy = () => {
    swal.close();
  };

  vm.$onInit = () => {
    const searchQuery = $location.absUrl().split('?')[1];
    // NonAgent Notification Approval Logic is also stored in query
    // This logic is kept for old version email link
    const { nonAgentApprovalHash: approvalHash, ...queryObj } =
      qs.parse(searchQuery);
    if (approvalHash) $location.search(queryObj);
    const index = parseInt(queryObj.index, 10);
    vm.dashboardId = $routeParams.dashboardId;
    vm.retsId = $routeParams.retsId;

    if (Number.isNaN(parseInt(index, 10))) {
      vm.nextSearchQuery = '';
      vm.prevSearchQuery = '';
      vm.prevListingId = '';
      vm.nextListingId = '';
    } else {
      vm.nextSearchQuery = qs.stringify({ ...queryObj, index: index + 1 });
      vm.prevSearchQuery =
        index > 0 ? qs.stringify({ ...queryObj, index: index - 1 }) : '';

      ListingViewService.getNeighborListings(index, queryObj).then(data => {
        vm.prevListingId = data.prevListingId;
        vm.nextListingId = data.nextListingId;
      });
    }

    if (vm.listing && vm.listing.reps?.length && vm.listing._id) {
      const repIds = vm.listing.reps.map(rep => rep.id);
      const isAuthUserRepId = repIds.includes($rootScope.auth?.id);
      // Update the button only if the auth user is a listing agent
      if (!isAuthUserRepId) {
        store.dispatch(
          updateChatButtonSettings({
            repIds,
            listingId: vm.listing._id,
            listing: vm.listing,
            // We need to explicitly pass an empty string to avoid a stale partial update - rare case but needs to be handled
            userIdToCreateChannelId: '',
          }),
        );
      }
    }

    if (vm.listing.canEdit) {
      AppointmentConfigService.getListingAppointmentConfig(vm.listing.id)
        .then(appointmentConfig => {
          vm.appointmentConfig = appointmentConfig;
          vm.isListingAutoConfirm = vm.appointmentConfig.autoConfirm;

          if (appointmentConfig.access === 'Supra Lockbox') {
            AppointmentConfigService.getSupraLockboxAccessHours(
              appointmentConfig.instructions.supralockbox.serialNumber,
            ).then(accessHours => {
              vm.supraLockboxAccessHours = parseAccessHours(
                accessHours,
                appointmentConfig.instructions.supralockbox.productCode,
                true,
              );
            });
          }
        })
        .catch(console.error);
    }

    vm.auth = $rootScope.auth;

    vm.listingOrgHasFeature = feature =>
      vm.listing.org.features.includes(feature);

    vm.hasFeature = feature =>
      (Boolean(vm.auth?.assumingUserId || vm.auth?.isAssumedNotBB) &&
        vm.auth?.org.id === vm.listing.org.id) ||
      vm.listing.org.features.includes(feature);

    vm.timezoneStr = getTimezoneCode(vm.listing.timezone);

    // convert html strings like &amp;
    vm.listing = {
      ...vm.listing,
      line1: decode(vm.listing.line1),
      line2: decode(vm.listing.line2),
      client_remarks: decode(vm.listing.client_remarks),
      brokerage_remarks: decode(vm.listing.brokerage_remarks),
    };

    // This logic is kept for old version email link
    if (approvalHash) {
      ListingViewService.approveNonAgentForAppointment(approvalHash)
        .then(({ isCustomAgent, approvedBefore }) => {
          swal(
            approvedBefore
              ? 'This Contact Has Already Been Approved'
              : 'Contact Approved',
            approvedBefore
              ? 'This Contact has already been added and no further action is required.'
              : `This ${
                  isCustomAgent ? 'non-agent' : 'home inspector / appraiser'
                } has been approved for the appointment.`,
            'success',
          );
        })
        .catch(ErrorService.toastyHTTPErr);
    }

    vm.isListingAgent = vm.listing.reps.some(({ id }) => id === vm.auth.id);
    vm.isCustomContact =
      $rootScope.reactExternalAuth?.user.contactType === 'customagent' ||
      $rootScope.reactExternalAuth?.user.contactType === 'nonagent' ||
      // For disable the buttons in external dashboard
      $rootScope.reactExternalAuth?.user.contactType === 'concierge';

    vm.hasListingBranchAccess = [
      vm.auth.location,
      ...(vm.auth.locations || []),
    ].includes(vm.listing.location);

    vm.isInternalAdmin = !vm.external && isFullAccessUser(vm.auth);

    vm.accessPendingWarningLabel =
      vm.external ||
      (!vm.isInternalAdmin && vm.listing.isUnclaimed) ||
      (!vm.external &&
        vm.auth.org.id === vm.listing.org.id &&
        isAuthForRole({
          auth: vm.auth,
          locations: vm.listing.locations,
          reps: vm.listing.reps,
        }));

    vm.enableOpenHouses = false;

    vm.listingType =
      vm.listing.list_price_type || `For ${capitalize(vm.listing.subtype)}`;

    vm.listing.formattedAcres = Number(vm.listing.acres).toLocaleString(
      'en-US',
    );

    const formattedSqftToString = Number(vm.listing.sqft).toLocaleString(
      'en-US',
    );
    vm.listing.formattedSqft =
      formattedSqftToString === 'NaN' ? vm.listing.sqft : formattedSqftToString;
    if (vm.listing.rooms?.length) {
      // APP-1894 Angular generates '$$hashKey' attribute to the json objects, and below should remove them
      const parsedRooms = vm.listing.rooms.map(room =>
        JSON.parse(angular.toJson(room)),
      );

      vm.existingRooms = parsedRooms.filter(room => {
        const { id: _id, ...rest } = room || {};
        return Object.keys(rest).length > 0;
      });
    }

    vm.isListingUnclaimed = () => vm.listing.isUnclaimed;

    vm.isDisabledBtns = () => vm.isListingUnclaimed() && !vm.isInternalAdmin;

    vm.unclaimedTooltipText = type => {
      if (vm.isDisabledBtns()) {
        if (type === 'eye')
          return 'You cannot view this modal as this listing is not represented by BrokerBay.';

        if (type === 'showing')
          return 'You cannot Book a Showing as this listing is not represented by BrokerBay.';

        if (type === 'offer')
          return 'You cannot Register an Offer as this listing is not represented by BrokerBay.';
      }

      return '';
    };

    vm.showBookShowingBtn = () => {
      if (vm.external) return false;
      const listingOrgHasAppointmentFeature =
        vm.listing.org?.features.includes('APPOINTMENT');

      // Filter based on listing statuses
      if (
        vm.listing.isDeleted ||
        !listingOrgHasAppointmentFeature ||
        vm.listing.visibility === 'pending'
      ) {
        return false;
      }

      // For concierge
      if (!vm.auth.org) return false;

      const currentUserOrgMlsType = vm.auth.org.extendedMlsTypes;
      const isSameMlsType = vm.listing.org.mlsTypes.some(mlsType =>
        currentUserOrgMlsType.includes(mlsType),
      );

      const isSameOrAnsweringOrg =
        vm.auth.org._id === vm.listing.org._id ||
        isAssumeOrgType(vm.auth.org.type);

      // If user is from a different org and the listing is in a different province do not show button
      if (
        !isSameOrAnsweringOrg &&
        !isSameMlsType &&
        !isManualCoopUserWithAccess(vm.auth, vm.listing.org)
      ) {
        return false;
      }

      // Booking allowed for any user unless they are both from a different org and different province, users will be limited by who they can book a showing for.
      return true;
    };

    vm.showAddToTourBtn = () =>
      isUserLicensed(vm.auth) && vm.showBookShowingBtn();

    const isSameOrAnsweringOrg =
      vm.auth.org &&
      (vm.auth.org.id === vm.listing.org.id ||
        isAssumeOrgType(vm.auth.org.type));

    vm.showRegisterOfferBtn = () => {
      if (!vm.hasFeature('OFFERS')) return false;

      if (vm.listing.visibility === 'pending') return false;

      // For concierge
      if (!vm.auth.org) return false;

      const currentUserOrgMlsType = vm.auth.org.extendedMlsTypes;
      const isSameMlsType = vm.listing.org.mlsTypes.some(mlsType =>
        currentUserOrgMlsType.includes(mlsType),
      );

      // If user is from a different org and the listing is in a different province do not show button
      if (
        !isSameOrAnsweringOrg &&
        !isSameMlsType &&
        !isManualCoopUserWithAccess(vm.auth, vm.listing.org)
      ) {
        return false;
      }

      return true;
    };

    vm.showExpiry = () => {
      if (vm.external || vm.network || !isSameOrAnsweringOrg) {
        return false;
      }

      const isAgentOrTA = ['AGENT', 'TEAM_ADMIN'].includes(vm.auth.role);
      const isHiddenRep =
        Array.isArray(vm.listing.hiddenReps) &&
        vm.listing.hiddenReps.includes(vm.auth.id);
      const isSameTeam = vm.listing.reps.some(
        rep => rep.team === vm.auth.team || vm.auth.teams?.includes(rep.team),
      );

      return (
        !isAgentOrTA ||
        vm.isListingAgent ||
        isHiddenRep ||
        (isSameTeam && vm.auth.role === 'TEAM_ADMIN')
      );
    };

    vm.qf =
      vm.listing.country === 'USA' || vm.listing.roomUnit === 'ft' ? 3.28 : 1;

    if (
      vm.listing.reps &&
      vm.listing.reps[0] &&
      vm.listing.reps[0].location &&
      vm.listing.reps[0].address
    ) {
      vm.address =
        vm.listing.org.addresses[vm.listing.reps[0].location][
          vm.listing.reps[0].address
        ];
      vm.isAgentDisabled = vm.listing.reps[0].disabled;
    } else {
      vm.isAgentDisabled = false;
    }

    const { onboardingPortalId, id: orgId } = vm.listing.org;
    const needPortalAssociation = Boolean(
      featureEnabled('narSettlementFeature') && onboardingPortalId && orgId,
    );

    const getNarSettlementValue = () => {
      ListingViewService.getOnboardingPortalAssociation(
        onboardingPortalId,
        orgId,
      )
        .then(onboardingPortalAssociation => {
          vm.hasNarSettlementCommission =
            onboardingPortalAssociation?.narSettlementCommission;
        })
        .catch(error => {
          console.error('Error fetching onboarding portal association:', error);
          vm.hasNarSettlementCommission = true;
        });
    };

    const loadData = () => {
      if (vm.external) return;

      if (!vm.listing.canEdit && vm.listing.id) {
        AppointmentBookService.getExternalConfig(vm.listing.id).then(
          externalConfig => {
            vm.externalAppointmentConfig = externalConfig;
          },
        );
      }

      const setData = ({
        files,
        virtualTourConfig,
        teamContact,
        answeringSubscription,
        coopRes,
        portalAssocData,
      }) => {
        if (needPortalAssociation && portalAssocData) {
          vm.hasNarSettlementCommission =
            portalAssocData?.narSettlementCommission;
        }
        vm.isCoopAgent = coopRes.hasBookedShowing;
        if (answeringSubscription?.answeringServicePhone)
          vm.answeringSubscription = answeringSubscription;
        vm.enableOpenHouses = vm.listing.org?.settings?.enableOpenHouses;
        const images = files
          .filter(file => file.isPhoto)
          .sort((a, b) => a.order - b.order)
          .map(image => image.url);
        vm.assets = ListingViewService.generateAssets(
          vm.listing,
          images,
          virtualTourConfig,
        );
        if ($rootScope.hasFeature('TEAMS')) vm.teamContact = teamContact;
        vm.attachments = files
          .filter(file => file.isAttachment)
          .map(file => ({
            uid: file.id,
            url: file.url,
            filename: file.name,
            mimetype: file.filetype,
            size: file.size,
          }));

        if (vm.listingOrgHasFeature('APPOINTMENT') && vm.hasFeature('OFFERS')) {
          ListingViewService.getOfferDetails(vm.listing.id).then(
            offerDetails => {
              vm.offerDetails = offerDetails;
            },
          );
        }

        if (vm.listing.expiry) {
          vm.listing.expiry = moment(vm.listing.expiry).format('MMM DD, YYYY');
        }
        if (vm.listing.coming_soon_date) {
          vm.listing.coming_soon_date = moment(
            vm.listing.coming_soon_date,
          ).format('MMM DD, YYYY');
        }
        if (vm.listing.availability_history) {
          vm.listing.availability_history = vm.listing.availability_history.map(
            history => {
              ListingViewService.formatHistory(history);
              return history;
            },
          );
        }
        vm.dataLoaded = true;
      };

      if (vm.listingInfo) {
        setData({
          files: vm.listingInfo.listingFiles,
          virtualTourConfig: vm.listingInfo.virtualTourInfo,
          teamContact: vm.listingInfo.answeringSubscription,
          answeringSubscription: vm.listingInfo.hasBookedShowing,
          coopRes: vm.listingInfo.listingTeam,
          portalAssocData: vm.listingInfo.portalAssocData,
        });
      } else {
        $q.all([
          ListingViewService.getListingFiles(vm.listing.id),
          ListingViewService.getVirtualTourConfig(vm.listing.id),
          ListingViewService.getTeamContact(vm.listing.team),
          ListingViewService.getListingAnsweringSubscription(vm.listing.id),
          ListingViewService.isCoopAgent(vm.listing.id),
          needPortalAssociation ? getNarSettlementValue() : undefined,
        ]).then(
          ([
            files,
            virtualTourConfig,
            teamContact,
            answeringSubscription,
            coopRes,
            onboardingPortalAssociation,
          ]) => {
            if (needPortalAssociation) {
              vm.hasNarSettlementCommission =
                onboardingPortalAssociation?.narSettlementCommission;
            }
            setData({
              files,
              virtualTourConfig,
              teamContact,
              answeringSubscription,
              coopRes,
            });
          },
        );
      }
    };

    if (vm.external) {
      if (needPortalAssociation) {
        getNarSettlementValue();
      }

      if (vm.dashboardId && vm.listing.id) {
        appointmentListsService
          .getExternalListingConfig(vm.dashboardId, vm.listing.id)
          .then(externalConfig => {
            vm.externalAppointmentConfig = externalConfig;
          });
      } else if (vm.retsId) {
        AppointmentBookService.getRetsConfig(vm.retsId).then(externalConfig => {
          vm.externalAppointmentConfig = externalConfig;
        });
      }

      vm.enableOpenHouses = vm.listing.org?.settings?.enableOpenHouses;
      vm.dataLoaded = true;
    } else {
      loadData();
    }
  };

  vm.isAgent = $rootScope.auth?.role === UserRoles.Agent;
  vm.isUnverified = $rootScope.auth?.isUnverified;
  vm.isListingAgent =
    vm.listing?.reps.filter(rep => rep.id === $rootScope.auth?.id).length > 0;

  vm.formatPhoneNumber = formatPhoneNumber;

  vm.networkBookLink = link => {
    const endpoint =
      link === 'offer' ? 'offer/new?view=listing' : 'appointments/book';
    return vm.auth.org &&
      vm.auth.org.features.includes('APPOINTMENT') &&
      !(vm.isAgent && !vm.isListingAgent && vm.isUnverified)
      ? `#/listing/${vm.listing.id}/${endpoint}`
      : undefined;
  };

  vm.handleWarningModal = () => {
    if (vm.isAgent && !vm.isListingAgent && vm.isUnverified) {
      Modal.warning(UNVERIFIED_AGENT_MODAL);
    }
  };

  vm.handleExternalBookingAppointment = () => {
    if (
      vm.listingOrgHasFeature('PRE_BOOKING') &&
      vm.externalAppointmentConfig?.preBookingInformation
    ) {
      Modal.confirm({
        icon: null,
        title: 'Important Information',
        content: `${vm.externalAppointmentConfig.preBookingInformation}`,
        okText: 'Continue to booking',
        cancelText: 'Cancel',
        closable: true,
        onOk() {
          window.location.href = vm.getExternalBookingLink();
        },
      });
      return;
    }

    window.location.href = vm.getExternalBookingLink();
  };

  vm.handleBookAppointment = () => {
    if (
      vm.auth.org &&
      vm.auth.org.features.includes('APPOINTMENT') &&
      !(vm.isAgent && !vm.isListingAgent && vm.isUnverified)
    ) {
      const preBookingInformation =
        vm.appointmentConfig?.preBookingInformation ||
        vm.externalAppointmentConfig?.preBookingInformation;
      if (vm.listingOrgHasFeature('PRE_BOOKING') && preBookingInformation) {
        Modal.confirm({
          icon: null,
          title: 'Important Information',
          content: `${preBookingInformation}`,
          okText: 'Continue to booking',
          cancelText: 'Cancel',
          closable: true,
          onOk() {
            window.location.href = `#/listing/${vm.listing.id}/appointments/book`;
          },
        });
        return;
      }

      window.location.href = `#/listing/${vm.listing.id}/appointments/book`;
      return;
    }

    vm.handleWarningModal();
  };

  vm.showActionButtons = () => {
    if (!$rootScope.auth || !vm.listing) return false;

    return vm.dataLoaded && !vm.newListingModal && vm.listing.canEdit;
  };

  vm.showIncludedExcluded = () => {
    const { included_excluded } = vm.listing?.freehold || {};
    if (
      vm.listing?.type === 'freehold' &&
      vm.listing?.subtype === 'lease' &&
      included_excluded
    ) {
      return Object.values(included_excluded).some(Boolean);
    }
    return false;
  };

  vm.isFutureOpenHouse = i => {
    const ohDate = utcToZonedTime(
      new Date(vm.listing.openHouses[i].timeTo),
      vm.listing.timezone,
    );
    return isBefore(new Date(), ohDate);
  };

  vm.getPets = () => {
    if (!vm.listing || !vm.listing.condo) return;
    if (vm.listing.condo.pets_allowed === 'N') return 'No';
    if (vm.listing.condo.pets_allowed === 'Restrict') return 'Restricted';
    return '';
  };

  vm.publishToMyBrokerage = () => {
    swal(
      {
        title: 'Publish to My Brokerage',
        text: 'Are you sure you want to publish and group page this listing to My Brokerage?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Yes',
        closeOnConfirm: true,
      },
      () => {
        // Convert to service
        vm.dataLoaded = false;
        ListingViewService.setDistribution(vm.listing.id, 'brokerage', 'new')
          .then(() => {
            $location.path('listing/list/brokerage/0');
          })
          .catch(err => {
            ErrorService.toastyHTTPErr(err);
          }); // Toast
      },
    );
  };

  vm.removeFromNetwork = () => {
    swal(
      {
        title: 'Remove from Network',
        text: 'Are you sure you want to remove this listing from the network?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Yes',
        closeOnConfirm: true,
      },
      () => {
        // Convert to service
        vm.dataLoaded = false;
        ListingViewService.setDistribution(vm.listing.id, 'brokerage', 'paged')
          .then(newHistory => {
            ModalService.closeNetworkModal(true);
            vm.listing.visibility = 'brokerage';
            vm.listing.pageStatus = 'paged';
            vm.listing.networkAt = null;
            ListingViewService.formatHistory(newHistory);
            vm.listing.availability_history.push(newHistory);
          })
          .catch(err => {
            ErrorService.toastyHTTPErr(err);
          }) // Toast
          .finally(() => {
            vm.dataLoaded = true;
          });
      },
    );
  };

  vm.externalSellerUsers = [
    'client',
    'seller',
    'tenant',
    'buyer',
    'landlord/manager',
  ];

  vm.showingsMadeEasyPhase2Feature = featureEnabled(
    'showingsMadeEasyPhase2Feature',
  );

  vm.preBookingNotificationFeature = featureEnabled(
    'preBookingNotificationFeature',
  );

  vm.displayShowingOfferButtons = () =>
    vm.external &&
    !vm.isViewListing &&
    !vm.externalSellerUsers.includes(
      $rootScope.reactExternalAuth.user.contactType,
    ) &&
    (vm.hasFeature('OFFERS') || vm.hasFeature('APPOINTMENT')) &&
    (vm.dashboardId || vm.retsId);

  vm.getExternalLink = () =>
    `/external/#/${vm.dashboardId ? 'dash' : 'rets'}/${
      vm.dashboardId ?? vm.retsId
    }`;

  vm.getExternalOfferLink = () =>
    `${vm.getExternalLink()}/${
      vm.dashboardId ? 'offers' : 'appointments/offer'
    }`;

  vm.getExternalBookingLink = () =>
    `${vm.getExternalLink()}/${vm.dashboardId ? 'book' : 'appointments/book'}`;

  vm.openContactDrawer = $rootScope.openContactDrawer;
  vm.getLabel = field =>
    $rootScope.labelOverrideMap[parseLabelFromString(field)]?.label || field;
  vm.displayedField = () =>
    vm.getLabel('Neighborhood Display')?.trim().toUpperCase() === 'COUNTY'
      ? 'county'
      : 'neighborhood';

  $rootScope.$watch('labelOverrideMap', () => {
    vm.getLabel = field =>
      $rootScope.labelOverrideMap[parseLabelFromString(field)]?.label || field;
  });

  vm.maintenanceOptions = [];
  vm.maintenanceOptions.heat = 'Heat Included';
  vm.maintenanceOptions.hydro = 'Hydro Included';
  vm.maintenanceOptions.water = 'Water Included';
  vm.maintenanceOptions.cable = 'Cable T.V. Included';
  vm.maintenanceOptions.cac = 'CAC Included';
  vm.maintenanceOptions.building_ins = 'Building Ins. Included';
  vm.maintenanceOptions.parking = 'Parking';
  vm.maintenanceOptions.cac = 'Heat Included';
  vm.maintenanceOptions.cac = 'Heat Included';

  vm.includedExcluded = [];
  vm.includedExcluded.all_inclusive = 'All Inclusive';
  vm.includedExcluded.heat = 'Heat Included';
  vm.includedExcluded.hydro = 'Hydro Included';
  vm.includedExcluded.water = 'Water Included';
  vm.includedExcluded.gas = 'Gas Included';
  vm.includedExcluded.cable = 'Cable T.V. Included';
  vm.includedExcluded.phone = 'Phone Included';
  vm.includedExcluded.cac = 'CAC Included';
}
